import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Loader2, Inbox, ArrowUpRight, X } from "lucide-react";
import { api, API, getToken } from "../lib/api";

/* Data hook */
export function useFetch(path, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(path);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { reload(); }, [reload]);
  return { data, loading, error, reload, setData };
}

export const Card = ({ children, className = "", hover = false, ...props }) => (
  <div
    className={`rounded-2xl border border-[#1E2127] bg-[#0E1013] ${hover ? "transition-colors duration-300 hover:border-[#2A2D34]" : ""} ${className}`}
    {...props}
  >
    {children}
  </div>
);

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay, ease: [0.16, 1, 0.3, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const BADGE = {
  "Not Started": "bg-[#1A1D22] text-[#6B7280]",
  Upcoming: "bg-[#1A1D22] text-[#8A939B]",
  "In Progress": "bg-[#C6FF00]/15 text-[#C6FF00] border border-[#C6FF00]/30",
  "Awaiting Client": "bg-amber-500/15 text-amber-300 border border-amber-500/30",
  "Awaiting Approval": "bg-amber-500/15 text-amber-300 border border-amber-500/30",
  "Revision Requested": "bg-orange-500/15 text-orange-300 border border-orange-500/30",
  Completed: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
  Submitted: "bg-[#1A1D22] text-[#8A939B]",
  "Under Review": "bg-blue-500/15 text-blue-300 border border-blue-500/30",
  Reviewing: "bg-blue-500/15 text-blue-300 border border-blue-500/30",
  Accepted: "bg-emerald-500/15 text-emerald-300",
  Scheduled: "bg-blue-500/15 text-blue-300",
  ready_for_review: "bg-[#C6FF00]/15 text-[#C6FF00] border border-[#C6FF00]/30",
  approved: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
  changes_requested: "bg-orange-500/15 text-orange-300 border border-orange-500/30",
  Paid: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
  "Partially Paid": "bg-amber-500/15 text-amber-300 border border-amber-500/30",
  Unpaid: "bg-orange-500/15 text-orange-300 border border-orange-500/30",
  Available: "bg-[#1A1D22] text-[#8A939B]",
  Signed: "bg-emerald-500/15 text-emerald-300",
  Succeeded: "bg-emerald-500/15 text-emerald-300",
  Published: "bg-[#C6FF00]/15 text-[#C6FF00] border border-[#C6FF00]/30",
  "Not Published": "bg-[#1A1D22] text-[#8A939B]",
  new: "bg-[#C6FF00]/15 text-[#C6FF00] border border-[#C6FF00]/30",
  contacted: "bg-amber-500/15 text-amber-300 border border-amber-500/30",
  completed: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
  converted: "bg-violet-500/15 text-violet-300 border border-violet-500/30",
};

const LABELS = { ready_for_review: "Ready for Review", approved: "Approved", changes_requested: "Changes Requested", new: "New", contacted: "Contacted", completed: "Completed", converted: "Converted" };

export const Badge = ({ status, className = "" }) => (
  <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide whitespace-nowrap ${BADGE[status] || "bg-[#1A1D22] text-[#8A939B]"} ${className}`}>
    {LABELS[status] || status}
  </span>
);

export const Loader = ({ label = "Loading" }) => (
  <div className="flex items-center justify-center py-24 text-[#8A939B]" data-testid="loader">
    <Loader2 className="h-6 w-6 animate-spin mr-3" /> <span className="text-sm">{label}…</span>
  </div>
);

export const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse rounded-xl bg-[#14171C] ${className}`} />
);

export const EmptyState = ({ icon: Icon = Inbox, title, body, action }) => (
  <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[#1E2127] bg-[#0E1013]/50 py-16 px-6 text-center" data-testid="empty-state">
    <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-[#1E2127] bg-[#111317]">
      <Icon className="h-6 w-6 text-[#4B5563]" />
    </div>
    <h3 className="text-lg font-semibold text-white">{title}</h3>
    {body && <p className="mt-2 max-w-sm text-sm text-[#8A939B]">{body}</p>}
    {action && <div className="mt-6">{action}</div>}
  </div>
);

export const Button = ({ children, variant = "primary", className = "", as: As = "button", ...props }) => {
  const base = "inline-flex items-center justify-center gap-2 rounded-full text-sm font-semibold transition-colors duration-200 disabled:opacity-50 disabled:pointer-events-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C6FF00]";
  const variants = {
    primary: "bg-[#C6FF00] text-[#0A0C0F] hover:bg-white px-5 py-2.5",
    ghost: "border border-[#2A2D34] text-white hover:border-[#C6FF00] hover:text-[#C6FF00] px-5 py-2.5",
    subtle: "bg-[#14171C] text-white hover:bg-[#1A1D22] px-5 py-2.5",
    danger: "border border-red-500/40 text-red-400 hover:bg-red-500/10 px-5 py-2.5",
  };
  return <As className={`${base} ${variants[variant]} ${className}`} {...props}>{children}</As>;
};

export const Field = ({ label, as = "input", className = "", ...props }) => {
  const Cmp = as;
  return (
    <label className="block">
      {label && <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#8A939B]">{label}</span>}
      <Cmp
        className={`w-full rounded-xl bg-[#0A0C0F] border border-[#1E2127] px-4 py-3 text-sm text-white placeholder:text-[#4B5563] focus:border-[#C6FF00] focus:outline-none focus:ring-1 focus:ring-[#C6FF00] transition-colors ${as === "textarea" ? "resize-none" : ""} ${className}`}
        {...props}
      />
    </label>
  );
};

export const Progress = ({ value }) => (
  <div className="h-2.5 w-full overflow-hidden rounded-full bg-[#14171C]">
    <motion.div
      initial={{ width: 0 }}
      animate={{ width: `${value}%` }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
      className="h-full rounded-full bg-gradient-to-r from-[#7CB800] to-[#C6FF00]"
    />
  </div>
);

export const PageHeader = ({ eyebrow, title, subtitle, actions }) => (
  <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
    <div>
      {eyebrow && <p className="mb-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#C6FF00]">{eyebrow}</p>}
      <h1 className="text-3xl font-bold tracking-tight text-white md:text-4xl">{title}</h1>
      {subtitle && <p className="mt-2 max-w-2xl text-sm text-[#8A939B]">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-3">{actions}</div>}
  </div>
);

export { ArrowUpRight };

export const Modal = ({ open, onClose, title, children, testid }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid={testid}>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} onClick={onClose} className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-lg rounded-2xl border border-[#1E2127] bg-[#0E1013] p-6 shadow-2xl"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="modal-close"
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-lg text-[#8A939B] transition-colors hover:bg-[#1A1D22] hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
        {title && <h3 className="mb-4 pr-8 text-xl font-bold text-white">{title}</h3>}
        {children}
      </motion.div>
    </div>
  );
};

export const Select = ({ label, options, className = "", ...props }) => (
  <label className="block">
    {label && <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#8A939B]">{label}</span>}
    <select
      className={`w-full rounded-xl bg-[#0A0C0F] border border-[#1E2127] px-4 py-3 text-sm text-white focus:border-[#C6FF00] focus:outline-none transition-colors ${className}`}
      {...props}
    >
      {options.map((o) => (
        <option key={o} value={o} className="bg-[#0E1013]">{o}</option>
      ))}
    </select>
  </label>
);

export const isImageType = (t = "", name = "") =>
  /image\/(png|jpe?g|gif|webp|svg)/i.test(t) || /\.(png|jpe?g|gif|webp|svg)$/i.test(name);

export function downloadBlob(path, filename, onError) {
  fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${getToken()}` } })
    .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      const el = document.createElement("a"); el.href = url; el.download = filename; el.click();
      URL.revokeObjectURL(url);
    })
    .catch(() => onError && onError());
}

export const AuthImage = ({ path, alt = "", className = "" }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let objUrl; let active = true;
    fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => (r.ok ? r.blob() : Promise.reject()))
      .then((b) => { if (active) { objUrl = URL.createObjectURL(b); setSrc(objUrl); } })
      .catch(() => {});
    return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [path]);
  return src
    ? <img src={src} alt={alt} className={className} />
    : <div className={`animate-pulse bg-[#14171C] ${className}`} />;
};
